import { Trash } from 'lucide-react';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/Tooltip.tsx';
import useGetCostCategoryUsage from '@/features/dashboard/costCategories/hooks/useGetCostCategoryUsage.ts';
import { CostCategoryDto } from '@/features/dashboard/costCategories/types/CostCategoryDto.ts';
import { pluralize } from '@/utils/pluralize.ts';

interface CostCategoryDeleteButtonProps {
  category: CostCategoryDto;
  onDelete: () => void;
}

/**
 * Usunięcie jest twarde i backend odmawia go (400), gdy kategoria ma wydatki.
 * Pytamy o użycie, żeby nie stawiać operatora przed przyciskiem, który tylko
 * zwróci błąd — zamiast tego zostaje mu archiwizacja.
 */
const CostCategoryDeleteButton = ({
  category,
  onDelete,
}: CostCategoryDeleteButtonProps) => {
  const { data: usage, isLoading } = useGetCostCategoryUsage(category.id);

  if (isLoading || !usage || usage.inUse) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {/* Wyłączony przycisk nie wysyła zdarzeń myszy, więc dymek bez opakowania nie wstaje. */}
          <span>
            <ActionButton
              tone="danger"
              size="toolbar"
              iconOnly
              disabled
              aria-label="Usuń kategorię"
            >
              <Trash />
            </ActionButton>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {usage?.inUse
            ? `Kategoria jest używana przez ${pluralize(usage.expenseCount, 'wydatek', 'wydatki', 'wydatków')}, zarchiwizuj ją zamiast usuwać`
            : 'Sprawdzanie użycia kategorii...'}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <ActionButton
          tone="danger"
          size="toolbar"
          iconOnly
          aria-label="Usuń kategorię"
          onClick={onDelete}
        >
          <Trash />
        </ActionButton>
      </TooltipTrigger>
      <TooltipContent>Usuń kategorię</TooltipContent>
    </Tooltip>
  );
};

export default CostCategoryDeleteButton;
